import './HPRing.css';

/**
 * Helper function to create SVG arc path
 * @param {number} cx - Center X
 * @param {number} cy - Center Y
 * @param {number} r - Radius
 * @param {number} startAngle - Start angle in degrees
 * @param {number} endAngle - End angle in degrees
 */
const describeArc = (cx, cy, r, startAngle, endAngle) => {
  // Convert angles to radians
  const startRad = (startAngle - 90) * Math.PI / 180;
  const endRad = (endAngle - 90) * Math.PI / 180;

  // Calculate start and end points
  const x1 = cx + r * Math.cos(startRad);
  const y1 = cy + r * Math.sin(startRad);
  const x2 = cx + r * Math.cos(endRad);
  const y2 = cy + r * Math.sin(endRad);

  // Determine if arc should be drawn the long way
  const largeArcFlag = endAngle - startAngle <= 180 ? 0 : 1;

  return `M ${x1} ${y1} A ${r} ${r} 0 ${largeArcFlag} 1 ${x2} ${y2}`;
};

/**
 * HPRing - Circular HP indicator that wraps any content
 * Displays segmented ring around children showing HP as individual segments
 *
 * @param {number} currentHP - Current HP value
 * @param {number} maxHP - Maximum HP value
 * @param {number} size - Size of the container in pixels (default: 40)
 * @param {React.ReactNode} children - Content to display inside the ring
 */
const HPRing = ({ currentHP, maxHP, size = null, children }) => {
  // Calculate HP percentage for color
  const percent = maxHP > 0 ? (currentHP / maxHP) * 100 : 100;

  // Get color based on HP percentage
  const getColor = (pct) => {
    if (pct >= 60) return '#4caf50'; // Green - healthy
    if (pct >= 30) return '#ffc107'; // Yellow - wounded
    return '#ef4444'; // Red - critical
  };

  const filledColor = getColor(percent);
  const emptyColor = 'rgba(255, 255, 255, 0.15)';

  // Tooltip text
  const tooltipText = `${currentHP}/${maxHP} HP (${Math.round(percent)}%)`;

  // Generate segments
  const segments = [];
  const gapAngle = maxHP > 1 ? 12 : 0; // Gap between segments (bigger gap for visibility)
  const totalGapAngle = gapAngle * maxHP;
  const segmentAngle = (360 - totalGapAngle) / maxHP;

  for (let i = 0; i < maxHP; i++) {
    const startAngle = i * (segmentAngle + gapAngle);
    const endAngle = startAngle + segmentAngle;
    const isFilled = i < currentHP;

    segments.push({
      startAngle,
      endAngle,
      filled: isFilled,
      key: i
    });
  }

  return (
    <div
      className="hp-ring"
      style={size ? { width: size, height: size } : undefined}
      title={tooltipText}
    >
      {/* SVG ring with segments */}
      <svg className="hp-ring-svg" viewBox="0 0 36 36">
        {segments.map((seg) => (
          <path
            key={seg.key}
            d={describeArc(18, 18, 16, seg.startAngle, seg.endAngle)}
            fill="none"
            stroke={seg.filled ? filledColor : emptyColor}
            strokeWidth="2.5"
            strokeLinecap="butt"
            className="hp-ring-segment"
          />
        ))}
      </svg>

      {/* Content (UnitIcon or any children) */}
      <div className="hp-ring-content">
        {children}
      </div>
    </div>
  );
};

export default HPRing;
